import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import * as _ from 'lodash-es';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class EcThresholdSetupService {

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
  }

  private buildFormData(data: any): string {
    const formData = new URLSearchParams();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return formData.toString();
  }

  save(payload: any, filterParams: any, rowData: any): Observable<any> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}/saveJsonObject?action_code=nddq6c`;

    const jsonSettings = {
      tax_year: filterParams?.tax_year
    };

    const params = _.merge({}, filterParams);
    params.json_data = JSON.stringify(payload);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.is_issue_key_enabled = 'N';

    return this.http.post(url, this.buildFormData(params), { headers: this.getHeaders() })
      .pipe(map(response => response as any));
  }

  update(payload: any, filterParams: any, rowData: any): Observable<any> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}/saveJsonObject?action_code=56p7mo`;

    const params = _.merge({}, filterParams);
    params.json_data = JSON.stringify(payload);
    params.is_issue_key_enabled = 'N';

    return this.http.post(url, this.buildFormData(params), { headers: this.getHeaders() })
      .pipe(map(response => response as any));
  }

  delete(tableId: string, filterParams: any, rowData: any): Observable<any> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}/saveJsonObject?action_code=nz85y7`;

    const params = _.merge({}, filterParams);
    params.tax_year = filterParams?.tax_year;
    params.scenario = filterParams?.scenario;
    params.jcd_key = filterParams?.jcd_key;
    params.scenarioCode = 'RAF';
    params.table_id = tableId;
    params.is_issue_key_enabled = 'N';

    return this.http.post(url, this.buildFormData(params), { headers: this.getHeaders() })
      .pipe(map(response => response as any));
  }

  loadThresholdRefreshType(baseUrl: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32863,
      action_code: '1mv9vb',
      tax_year: filterParams?.tax_year || rowData?.TAX_YEAR,
      scenario: filterParams?.scenario || rowData?.SCENARIO,
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY,
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY,
      screen_type: filterParams?.screen_type || 'grid',
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || ''
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        if (response.jsonObject) {
          return response.jsonObject;
        } else if (response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response.result) {
          return response.result;
        }
        return [];
      })
    );
  }

  loadThresholdClassification(baseUrl: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32864,
      action_code: 'g3g3mz',
      tax_year: filterParams?.tax_year || rowData?.TAX_YEAR,
      scenario: filterParams?.scenario || rowData?.SCENARIO,
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY,
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY,
      screen_type: filterParams?.screen_type || 'grid',
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || ''
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        if (response.jsonObject) {
          return response.jsonObject;
        } else if (response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response.result) {
          return response.result;
        }
        return [];
      })
    );
  }

  loadThresholdTableType(baseUrl: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32865,
      action_code: 's05y4h',
      tax_year: filterParams?.tax_year || rowData?.TAX_YEAR,
      scenario: filterParams?.scenario || rowData?.SCENARIO,
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY,
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY,
      screen_type: filterParams?.screen_type || 'grid',
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || ''
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        if (response.jsonObject) {
          return response.jsonObject;
        } else if (response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response.result) {
          return response.result;
        }
        return [];
      })
    );
  }

  loadThresholdValue(baseUrl: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32866,
      action_code: '0ibo0u',
      tax_year: filterParams?.tax_year || rowData?.TAX_YEAR,
      scenario: filterParams?.scenario || rowData?.SCENARIO,
      jcd_key: filterParams?.jcd_key || rowData?.JCD_KEY,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData?.JCD_TA_KEY,
      activity_key: filterParams?.activity_key || rowData?.ACTIVITY_KEY,
      screen_key: filterParams?.screen_key || rowData?.SCREEN_KEY,
      screen_type: filterParams?.screen_type || 'grid',
      is_locked: filterParams?.is_locked || 'N',
      issue_id: filterParams?.issue_id || '',
      issue_short_desc: filterParams?.issue_short_desc || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || ''
    };

    // 🚀 CRITICAL: Build URL with query parameters for GET request
    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${baseUrl}/loadJsonObject?${queryParams.toString()}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        if (response.jsonObject) {
          return response.jsonObject;
        } else if (response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response.result) {
          return response.result;
        }
        return [];
      })
    );
  }


  private formatYear(year: any): string {
    if (!year) return '';
    if (typeof year === 'number') {
      return year.toString();
    }
    if (year instanceof Date) {
      return year.getFullYear().toString();
    }
    return year.toString();
  }

  private getBaseUrl(): string {
    // This should be injected from the headerData or baseURLs
    return 'https://api.example.com'; // Placeholder - should be replaced with actual base URL
  }

  // EC Refresh Setup specific methods
  loadScenarioTypeCode(filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR
    };

    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;
    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  loadScenarioDesc(selectedValue: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: selectedValue
    };

    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;
    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  loadGroupObjKey(selectedVal1: string, selectedVal2: string, filterParams: any, rowData: any): Observable<any> {
    const baseParams: any = {
      action_id: 32735,
      action_code: '73pnl0',
      activity_key: filterParams?.activity_key || rowData.ACTIVITY_KEY || 5124,
      is_locked: 'N',
      issue_id: '',
      issue_id_for_tx_save: '',
      issue_short_desc: '',
      jcd_key: rowData.JCD_KEY || 250,
      jcd_ta_key: filterParams?.jcd_ta_key || rowData.JCD_TA_KEY || 5133,
      scenario: filterParams?.scenario || rowData.SCENARIO || 74,
      screen_key: filterParams?.screen_key || rowData.SCREEN_KEY || 11163,
      screen_type: 'grid',
      tax_year: filterParams?.tax_year ?? rowData?.TAX_YEAR,
      scenario_type_code: selectedVal1,
      scenario_desc: selectedVal2
    };

    const queryParams = new URLSearchParams();
    Object.keys(baseParams).forEach(key => {
      if (baseParams[key] !== null && baseParams[key] !== undefined) {
        queryParams.set(key, baseParams[key]);
      }
    });

    const url = `${this.getBaseUrl()}/loadJsonObject?${queryParams.toString()}`;
    return this.http.get<any>(url).pipe(
      map(response => response.jsonObject || [])
    );
  }

  saveRefreshSetupForm(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=upobod`;
    
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        formData.set(key, params[key]);
      }
    });

    return this.http.post<any>(url, formData.toString(), { ...httpOptions, withCredentials: true });
  }

  updateRefreshSetupForm(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=upobod`;
    
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        formData.set(key, params[key]);
      }
    });

    return this.http.post<any>(url, formData.toString(), { ...httpOptions, withCredentials: true });
  }

  saveThresholdForm(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=nddq6c`;
    
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        formData.set(key, params[key]);
      }
    });

    return this.http.post<any>(url, formData.toString(), { ...httpOptions, withCredentials: true });
  }

  updateThresholdForm(
    baseUrl: string,
    params: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=56p7mo`;
    
    // 🚀 CRITICAL: Build payload - use globalService directly (not globalService?.globalParams)
    let finalParams = _.merge({}, globalService || {}, filterParams);
    finalParams = _.extend({ json_data: params.json_data }, finalParams);
    finalParams.is_issue_key_enabled = params.is_issue_key_enabled || 'N';
    
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    const formData = new URLSearchParams();
    Object.keys(finalParams).forEach(key => {
      if (finalParams[key] !== null && finalParams[key] !== undefined) {
        formData.set(key, finalParams[key]);
      }
    });

    return this.http.post<any>(url, formData.toString(), { ...httpOptions, withCredentials: true });
  }

  deleteThreshold(
    baseUrl: string,
    tableId: string,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=nz85y7`;
    
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    // 🚀 CRITICAL: Build payload - use globalService directly (not globalService?.globalParams)
    let params = _.merge({}, globalService || {}, filterParams);
    params = _.extend({ table_id: tableId }, params);
    params.is_issue_key_enabled = 'N';
    
    // 🚀 CRITICAL: Add required parameters that backend expects
    // Use filterParams first, then fallback to globalService
    params.tax_year = filterParams?.tax_year ?? globalService?.tax_year ?? rowData?.TAX_YEAR;
    params.scenario = filterParams?.scenario ?? globalService?.scenario ?? rowData?.SCENARIO;
    params.jcd_key = filterParams?.jcd_key ?? globalService?.jcd_key ?? rowData?.JCD_KEY;
    params.jcd_ta_key = filterParams?.jcd_ta_key || globalService?.jcd_ta_key || rowData?.JCD_TA_KEY || '';
    params.activity_key = filterParams?.activity_key || globalService?.activity_key || rowData?.ACTIVITY_KEY || '';
    params.screen_key = filterParams?.screen_key || globalService?.screen_key || rowData?.SCREEN_KEY || '';
    params.issue_id = filterParams?.issue_id || globalService?.issue_id || '';
    params.issue_short_desc = filterParams?.issue_short_desc || globalService?.issue_short_desc || '';
    params.issue_id_for_tx_save = filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || '';
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || globalService?.scenarioCode || globalService?.scenario_code || 'RAF';

    const formData = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        formData.set(key, params[key]);
      }
    });

    return this.http.post<any>(url, formData.toString(), { ...httpOptions, withCredentials: true });
  }

  convertDateFormats(jsonObj: any): any {
    if (jsonObj.BATCH_START_DATE) {
      jsonObj.BATCH_START_DATE = moment(jsonObj.BATCH_START_DATE).format('MM/DD/YYYY');
    }
    if (jsonObj.BATCH_END_DATE) {
      jsonObj.BATCH_END_DATE = moment(jsonObj.BATCH_END_DATE).format('MM/DD/YYYY');
    }
    if (jsonObj.DEFAULT_TAX_YEAR_BEGIN) {
      jsonObj.DEFAULT_TAX_YEAR_BEGIN = moment(jsonObj.DEFAULT_TAX_YEAR_BEGIN).format('MM/DD/YYYY');
    }
    if (jsonObj.DEFAULT_TAX_YEAR_END) {
      jsonObj.DEFAULT_TAX_YEAR_END = moment(jsonObj.DEFAULT_TAX_YEAR_END).format('MM/DD/YYYY');
    }
    return jsonObj;
  }
}

